/*
 * Copyright (c) 2022. Revo Digital
 * ---
 * Author: gabrielecavallo
 * File: PrecompiledFeature.ts
 * Project: mle-client-n
 * Committed last: 2022/11/25 @ 1126
 * ---
 * Description:
 */

import {EditorFeature} from "../../../common/EditorFeature";
import {Editor} from "../../../../observables/Editor";
import {
    MyLabelPrecompiledField,
    MyLabelPrecompiledFieldFactory,
    MyLabelPrecompiledFieldInput
} from "../factory/mylabel/MyLabelPrecompiledFieldFactory";
import {precompiledConfig, precompiledTableConfig} from "../../../../../config";
import {MyLabelPRCNameFactory} from "../factory/mylabel/MyLabelPRCNameFactory";
import {action, computed, makeObservable, observable} from "mobx";
import {ShapeTypes} from "../../../../../common/enums";
import {
    MyLabelPrecompiledTable,
    MyLabelPrecompiledTableFactory
} from "../factory/mylabel/MyLabelPrecompiledTableFactory";

export const MLPRC_FNAME = "mylabel-precompiled";

/**
 * Feature per gestire l'indice dei precompilati MyLabel
 */
export class MyLabelPrecompiledFeature extends EditorFeature<void> {
    constructor(parent: Editor) {
        super(MLPRC_FNAME, parent);

        // Load index
        this._fieldIndex = [];
        this._tableIndex = [];
        this._fieldFactory = new MyLabelPrecompiledFieldFactory(this._fieldIndex);
        this._tableFactory = new MyLabelPrecompiledTableFactory(this._tableIndex);

        makeObservable(this);
    }

    /*
    Indice con tutte le forme che contengono i campi precompilati
     */
    @observable
    private _fieldIndex: MyLabelPrecompiledField[];

    @computed
    public get fieldIndex(): MyLabelPrecompiledField[] {
        return this._fieldIndex;
    }

    /*
    Indice con tutte le tabelle precompilate
     */
    @observable
    private _tableIndex: MyLabelPrecompiledTable[];

    @computed
    public get tableIndex(): MyLabelPrecompiledTable[] {
        return this._tableIndex;
    }

    private _fieldFactory: MyLabelPrecompiledFieldFactory;

    /**
     * Restituisce la factory da utilizzare per creare una forma
     */
    public get fieldFactory(): MyLabelPrecompiledFieldFactory {
        return this._fieldFactory;
    }

    private _tableFactory: MyLabelPrecompiledTableFactory;

    /**
     * Restituisce la factory da utilizzare per creare una tabella precompilata
     */
    public get tableFactory(): MyLabelPrecompiledTableFactory {
        return this._tableFactory;
    }

    public getForGroup(shapeType: ShapeTypes): MyLabelPrecompiledField[] {
        return this._fieldIndex.filter(it => {
            return (it.additionalGroups && it.additionalGroups.includes(shapeType))
        });
    }

    public getPrecompiledNames(): string[] {
        return this._fieldIndex.map(it => it.name);
    }

    init(): boolean {
        this._fieldIndex = this.loadFieldIndex();
        this._tableIndex = this.loadTableIndex();

        return true;
    }

    destroy(): boolean {
        return true;
    }

    @action
    private loadFieldIndex(): MyLabelPrecompiledField[] {
        const raw = precompiledConfig as MyLabelPrecompiledFieldInput[];
        const nameFactory = new MyLabelPRCNameFactory();

        try {
            const index = raw.map(it => {
                return {
                    ...it,
                    name: nameFactory.calculateName(it)
                }
            });

            return index;
        } catch (e) {
            console.error(e);
            return [];
        }
    }

    @action
    private loadTableIndex(): MyLabelPrecompiledTable[] {
        return precompiledTableConfig as MyLabelPrecompiledTable[];
    }
}